import { alpha } from '@mui/material';
import { duration, fontFamilyDisplay, glory, grey, hazard, ms, radius } from '../../../theme/tokens';

/**
 * The garage look of the public home page: brushed steel under a shop light,
 * hazard striping, riveted plates and condensed sign lettering. Every colour
 * comes from the tokens; these only arrange them.
 */

/** Film grain: an SVG noise tile of faint light specks, for dark steel. */
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180'%3E" +
  "%3Cfilter id='g'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E" +
  "%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.09 0'/%3E%3C/filter%3E" +
  "%3Crect width='100%25' height='100%25' filter='url(%23g)'/%3E%3C/svg%3E\")";

/** Dark brushed steel under one overhead light. The same in both colour modes. */
export const steel = {
  backgroundColor: grey[950],
  backgroundImage: [
    `radial-gradient(ellipse 70% 60% at 50% -10%, ${alpha(grey[50], 0.1)} 0%, transparent 70%)`,
    GRAIN,
    `repeating-linear-gradient(180deg, ${alpha(grey[0], 0.02)} 0 1px, transparent 1px 4px)`,
  ].join(', '),
  color: grey[100],
} as const;

/** Yellow-and-black caution tape. */
export const hazardStripe = (height = 10) => ({
  height,
  backgroundImage: `repeating-linear-gradient(-45deg, ${hazard[500]} 0 14px, ${grey[950]} 14px 28px)`,
});

const BOLT = {
  light: { shine: grey[0], head: grey[300], rim: grey[400] },
  dark: { shine: alpha(grey[0], 0.4), head: grey[700], rim: grey[950] },
} as const;

/**
 * Four bolt heads in the corners of a plate, drawn over its content. Each is
 * lit from the top left: a glint, the domed head, and a darker rim.
 */
export const rivets = (tone: 'light' | 'dark', inset = 9) => {
  const { shine, head, rim } = BOLT[tone];
  const bolt = `radial-gradient(circle at 40% 40%, ${shine} 0 0.9px, ${head} 1.5px 3px, ${rim} 3.3px 4.1px, transparent 4.5px)`;
  return {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      backgroundImage: Array(4).fill(bolt).join(', '),
      backgroundSize: '10px 10px',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: [
        `left ${inset}px top ${inset}px`,
        `right ${inset}px top ${inset}px`,
        `left ${inset}px bottom ${inset}px`,
        `right ${inset}px bottom ${inset}px`,
      ].join(', '),
    },
  } as const;
};

/** Faint drafting-paper grid, for an absolutely placed layer behind content. */
export const draftingGrid = (line: string) =>
  ({
    position: 'absolute',
    inset: 0,
    pointerEvents: 'none',
    backgroundImage: `linear-gradient(${line} 1px, transparent 1px), linear-gradient(90deg, ${line} 1px, transparent 1px)`,
    backgroundSize: '32px 32px',
    backgroundPosition: 'center top',
    // Fades out top and bottom, so the grid never meets a section edge.
    maskImage: 'linear-gradient(180deg, transparent 0%, #000 18%, #000 82%, transparent 100%)',
    WebkitMaskImage: 'linear-gradient(180deg, transparent 0%, #000 18%, #000 82%, transparent 100%)',
  }) as const;

/** Red that reads on the surface: the lighter step on dark steel. */
export const red = (onDark: boolean) => (onDark ? glory[400] : glory[600]);

const sign = { fontFamily: fontFamilyDisplay, textTransform: 'uppercase' } as const;

/** Big condensed headline lettering; lines balanced, so no word is stranded. */
export const headline = {
  ...sign,
  fontWeight: 800,
  lineHeight: 0.92,
  letterSpacing: '0.005em',
  textWrap: 'balance',
} as const;

/** Body copy without a one-word last line. */
export const prose = { textWrap: 'pretty' } as const;

/** Small spaced-out labels, like a stencil on a toolbox. */
export const stencil = { ...sign, fontWeight: 700, fontSize: '0.8125rem', letterSpacing: '0.16em', lineHeight: 1.3 } as const;

/** Numbers on a gauge or work order. */
export const gaugeNumber = { fontFamily: fontFamilyDisplay, fontWeight: 800, fontVariantNumeric: 'tabular-nums' } as const;

const buttonBase = {
  ...sign,
  fontWeight: 700,
  fontSize: '1.0625rem',
  letterSpacing: '0.08em',
  borderRadius: `${radius.xs}px`,
  px: 3.5,
  py: 1.25,
  transition: `background-color ${ms(duration.fast)}, box-shadow ${ms(duration.fast)}, transform ${ms(duration.instant)}`,
  // Safety yellow reads on steel and on paper alike.
  '&:focus-visible': { outline: `3px solid ${hazard[400]}`, outlineOffset: 3 },
} as const;

/** The call to action: a red plate with a pressed-metal edge. */
export const redButton = {
  ...buttonBase,
  backgroundColor: glory[600],
  color: grey[0],
  boxShadow: `0 3px 0 ${glory[800]}`,
  '&:hover': { backgroundColor: glory[700], boxShadow: `0 3px 0 ${glory[800]}` },
  '&:active': { transform: 'translateY(2px)', boxShadow: `0 1px 0 ${glory[800]}` },
} as const;

/** Secondary action on steel. */
export const steelButton = {
  ...buttonBase,
  color: grey[100],
  border: `2px solid ${alpha(grey[100], 0.4)}`,
  '&:hover': { border: `2px solid ${grey[100]}`, backgroundColor: alpha(grey[100], 0.06) },
} as const;
